import { useEffect, useRef, useState } from 'react';
import { X, Loader2, FileText, Eye, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { DA, SunOrnament, SabrilDecorFrame, BrandMark } from '../theme/sabril-da';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'MA', label: '🇲🇦 Maroc' },
  { code: 'ES', label: '🇪🇸 Espagne' },
];

/**
 * ContractSignModal — listens to 'contract:open' with { offer } detail.
 * Only for economie + affaires. Client signs → PDF sent by email → Stripe (economie).
 */
export default function ContractSignModal() {
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState('economie');
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewRead, setPreviewRead] = useState(false);
  const [pulseChecks, setPulseChecks] = useState(false);
  const previewScrollRef = useRef(null);
  const [form, setForm] = useState({
    company_name: '', siret: '',
    firstName: '', lastName: '', email: '', phone: '',
    restaurant_name: '', address: '', postal_code: '', city: '', country: 'FR',
    signature_name: '',
    accept_terms: false, accept_price: false, accept_data: false, accept_signature: false,
  });

  useEffect(() => {
    const h = (e) => {
      const nextOffer = e.detail?.offer || 'economie';
      const nextPrefill = e.detail?.prefill || null;
      setOffer(nextOffer);
      setPrefill(nextPrefill);
      setError(''); setConfirmed(null);
      setPreviewOpen(false); setPreviewRead(false); setPulseChecks(false);
      // Pre-fill form fields from configurator prefill (contact) if provided
      if (nextPrefill?.contact) {
        setForm((f) => ({
          ...f,
          firstName: nextPrefill.contact.firstName || f.firstName,
          email: nextPrefill.contact.email || f.email,
          phone: nextPrefill.contact.phone || f.phone,
          restaurant_name: nextPrefill.contact.restaurant || f.restaurant_name,
        }));
      }
      // If user already read the preview on /contrat/:offer, inherit the flag
      if (window.__contractPreviewRead70) setPreviewRead(true);
      setOpen(true);
    };
    window.addEventListener('contract:open', h);
    return () => window.removeEventListener('contract:open', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Fetch preview HTML when user opens the inline preview
  useEffect(() => {
    if (!previewOpen || previewHtml) return;
    let alive = true;
    let url = `${API_URL}/api/contract/preview?offer=${offer}`;
    if (offer === 'configurateur' && prefill) {
      const params = new URLSearchParams({
        offer: 'configurateur',
        config_format: prefill.format || 'distanciel',
        config_country: prefill.country || '',
        config_chantiers: (prefill.chantiers || []).join(','),
        config_full_pack: String(!!prefill.full_pack),
      });
      if (prefill.total_ht != null) params.set('config_total_ht', String(prefill.total_ht));
      if (form.firstName) params.set('firstName', form.firstName);
      if (form.restaurant_name) params.set('restaurant_name', form.restaurant_name);
      url = `${API_URL}/api/contract/preview?${params.toString()}`;
    }
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        if (!alive) return;
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        setPreviewHtml(bodyMatch ? bodyMatch[1] : html);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [previewOpen, previewHtml, offer, prefill, form.firstName, form.restaurant_name]);

  // Trigger pulse animation on checkboxes when preview reaches 70%
  useEffect(() => {
    if (!previewRead) return;
    setPulseChecks(true);
    const t = setTimeout(() => setPulseChecks(false), 3200);
    return () => clearTimeout(t);
  }, [previewRead]);

  const onPreviewScroll = () => {
    const el = previewScrollRef.current;
    if (!el || previewRead) return;
    const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
    if (pct >= 0.7) setPreviewRead(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setError('');
    const required = ['company_name', 'firstName', 'lastName', 'email', 'phone', 'restaurant_name', 'address', 'postal_code', 'city', 'signature_name'];
    for (const k of required) if (!form[k]?.trim()) { setError('Remplis tous les champs.'); return; }
    if (!form.email.includes('@')) { setError('Email invalide.'); return; }
    if (!(form.accept_terms && form.accept_price && form.accept_data && form.accept_signature)) {
      setError('Coche les 4 cases obligatoires pour signer.'); return;
    }
    setLoading(true);
    try {
      const body = { offer, ...form };
      // Include configurator context when relevant
      if (offer === 'configurateur' && prefill) {
        body.config = {
          format: prefill.format,
          format_label: prefill.format_label,
          country: prefill.country || null,
          chantiers: prefill.chantiers || [],
          full_pack: !!prefill.full_pack,
          bundle_key: prefill.bundle_key || null,
          total_ht: prefill.total_ht,
        };
      }
      const res = await fetch(`${API_URL}/api/contract/sign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data && data.contract_id) {
        if (offer === 'economie' && data.stripe_url) {
          window.location.href = data.stripe_url; return;
        }
        // Configurateur < 10 000€ HT → Stripe checkout direct (Option C hybride)
        if (offer === 'configurateur' && data.stripe_url) {
          try { sessionStorage.removeItem('sabril_config_prefill'); } catch (_e) { /* ignore */ }
          window.location.href = data.stripe_url; return;
        }
        // Clear configurator prefill from sessionStorage once signed
        if (offer === 'configurateur') {
          try { sessionStorage.removeItem('sabril_config_prefill'); } catch (_e) { /* ignore */ }
        }
        setConfirmed(data);
        setLoading(false);
        return;
      }
      setError(data?.detail || 'Impossible d\'enregistrer la signature.');
    } catch (_err) {
      setError('Erreur réseau.');
    }
    setLoading(false);
  };

  if (!open) return null;

  const isEconomie = offer === 'economie';
  const isConfigurateur = offer === 'configurateur';
  const configHeaderPill = isConfigurateur ? (
    prefill?.format === 'audit48' ? 'AUDIT 48H · SUR SITE' :
    prefill?.format === 'terrain17' ? '17 JOURS · TERRAIN' :
    prefill?.format === 'hybride' ? '90J + 15J TERRAIN' :
    '90J · DISTANCIEL'
  ) : (isEconomie ? '48H · EXPRESS' : '90J · DISTANCIEL');
  const configHeaderSubtitle = isConfigurateur
    ? 'Contrat sur-mesure · vos chantiers pré-remplis · devis annexé sous 24h.'
    : (isEconomie ? 'Signature électronique · reçu par email · paiement Stripe juste après.' : 'Contrat de mission · devis annexé sous 24h.');
  const accent = DA.goldMain;
  const accentDark = DA.goldDeep;
  const accentBg = DA.goldSofter;

  const inp = { width: '100%', padding: '11px 13px', background: DA.paper, border: `1px solid ${DA.goldSoft}`, borderRadius: 10, fontSize: 14.5, color: DA.greenDeep, outline: 'none', fontFamily: DA.sans };
  const lbl = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: DA.goldDeep, marginBottom: 6, display: 'block', fontFamily: DA.sans };

  if (confirmed) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" style={{ background: 'rgba(11,43,36,0.55)', backdropFilter: 'blur(8px)' }} data-testid="contract-modal-confirmed">
        <div className="w-full sm:max-w-[440px] p-6 sm:p-8 text-center relative overflow-hidden" style={{ background: DA.ivory, borderRadius: '24px 24px 0 0', color: DA.greenDeep }}>
          <div className="flex justify-center mb-4">
            <SunOrnament size={28} />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5" style={{ background: DA.goldSofter, border: `1px solid ${DA.goldMain}` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: DA.goldMain }} />
            <span style={{ color: DA.goldDeep, fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase' }}>{isConfigurateur ? 'Inscription enregistrée' : 'Contrat signé'}</span>
          </div>
          <h2 style={{ fontFamily: DA.serif, fontSize: 26, fontWeight: 800, lineHeight: 1.05, marginBottom: 12, color: DA.greenDeep, letterSpacing: '-0.02em' }}>
            {isConfigurateur ? (
              <>Vous êtes sur la liste <span style={{ color: DA.goldMain }}>{form.firstName}</span>.</>
            ) : (
              <>Ton contrat est signé <span style={{ color: DA.goldMain }}>{form.firstName}</span>.</>
            )}
          </h2>
          <p style={{ color: DA.inkMuted, fontSize: 13, marginBottom: 20 }}>
            Réf : <strong style={{ color: DA.greenDeep }}>{confirmed.contract_id}</strong> · envoyé à <strong style={{ color: DA.greenDeep }}>{form.email}</strong>. Vous êtes inscrit à la liste d&apos;attente — Sabril vous recontacte personnellement dès qu&apos;une place se libère.
          </p>
          <button onClick={() => { setOpen(false); setConfirmed(null); }} className="w-full py-3 rounded-xl font-bold" style={{ background: DA.greenDeep, color: '#FFFFFF', fontFamily: DA.sans, letterSpacing: '0.14em', fontSize: 13, textTransform: 'uppercase' }}>OK, à bientôt</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }} style={{ background: 'rgba(11,43,36,0.55)', backdropFilter: 'blur(8px)' }} data-testid={`contract-modal-${offer}`}>
      <style>{`
        @keyframes signPulse {
          0%   { box-shadow: 0 0 0 0 ${accent}55, 0 0 0 0 ${accent}00; transform: scale(1); }
          40%  { box-shadow: 0 0 0 8px ${accent}22, 0 12px 24px -8px ${accent}55; transform: scale(1.015); }
          100% { box-shadow: 0 0 0 0 ${accent}00, 0 0 0 0 ${accent}00; transform: scale(1); }
        }
        .sign-pulse { animation: signPulse 1.6s ease-in-out 2; }
        .contract-preview-scope .kicker { text-transform: uppercase; letter-spacing: 2.5px; font-size: 8.5px; color: ${DA.goldMain}; font-weight: 700; margin: 0 0 4px; }
        .contract-preview-scope h1 { font-size: 15px; font-weight: 900; margin: 3px 0 10px; line-height: 1.1; color: ${DA.greenDeep}; font-family: "Playfair Display", Georgia, serif; letter-spacing: -0.01em; }
        .contract-preview-scope h2 { font-size: 11px; font-weight: 900; margin: 12px 0 4px; color: ${DA.greenDeep}; font-family: "Playfair Display", Georgia, serif; }
        .contract-preview-scope p { margin: 0 0 4px; text-align: justify; font-size: 10.5px; line-height: 1.5; color: ${DA.ink}; }
        .contract-preview-scope ul { padding-left: 16px; margin: 4px 0 6px; }
        .contract-preview-scope li { font-size: 10.5px; line-height: 1.5; margin: 2px 0; color: ${DA.ink}; }
        .contract-preview-scope .box { background: ${DA.ivoryDeep}; padding: 8px 10px; border-radius: 7px; margin: 6px 0; border: 0.5px solid ${DA.goldSoft}; }
        .contract-preview-scope .box p { margin: 2px 0; font-size: 10px; }
        .contract-preview-scope .label { font-size: 8px; text-transform: uppercase; letter-spacing: 1.3px; color: ${DA.goldDeep}; font-weight: 700; }
        .contract-preview-scope .val { font-weight: 700; color: ${DA.greenDeep}; }
        .contract-preview-scope .stamp { display: inline-block; padding: 2px 8px; background: ${DA.goldSofter}; border: 1px solid ${DA.goldMain}; border-radius: 999px; font-size: 8px; letter-spacing: 1.2px; color: ${DA.goldDeep}; font-weight: 700; text-transform: uppercase; margin-bottom: 6px; }
        .contract-preview-scope .signatures { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
        .contract-preview-scope .sig { flex: 1; min-width: 110px; border: 1px dashed ${DA.goldSoft}; padding: 8px 10px; border-radius: 7px; min-height: 50px; }
        .contract-preview-scope .sig-name { font-family: "Playfair Display", Georgia, serif; font-style: italic; font-size: 12px; color: ${DA.greenDeep}; margin-top: 3px; }
        .contract-preview-scope .sig-date { font-size: 8px; color: ${DA.inkFaint}; margin-top: 4px; }
      `}</style>
      <div className="w-full sm:max-w-[520px] max-h-[92vh] overflow-y-auto relative" style={{ background: DA.ivory, borderRadius: '24px 24px 0 0' }}>
        <SabrilDecorFrame awningTL awningBR={false} mimosa={false} palm awningSize={90} />
        <div className="sticky top-0 z-10 px-5 pt-4 pb-3 flex items-start justify-between" style={{ background: `linear-gradient(180deg, ${DA.ivory} 92%, transparent 100%)` }}>
          <div className="flex-1 pr-3">
            <p style={{ fontFamily: DA.sans, fontSize: 9.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800 }}>{configHeaderPill}</p>
            <h2 style={{ fontFamily: DA.serif, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: DA.greenDeep, marginTop: 4, lineHeight: 1.05 }}>Signer mon contrat<span style={{ color: DA.goldMain }}>.</span></h2>
            <p style={{ fontSize: 12.5, color: DA.inkMuted, marginTop: 4, fontFamily: DA.sans }}>{configHeaderSubtitle}</p>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full flex-shrink-0" style={{ background: DA.ivoryDeep, color: DA.greenDeep, border: `0.5px solid ${DA.goldSoft}` }} aria-label="Fermer" data-testid="contract-close"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="px-5 pb-8 space-y-3" data-testid={`contract-form-${offer}`}>
          <p style={lbl}>Société</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label style={lbl}>Raison sociale</label><input value={form.company_name} onChange={set('company_name')} placeholder="SARL / SAS…" style={inp} required data-testid="contract-company" /></div>
            <div><label style={lbl}>SIRET / ICE</label><input value={form.siret} onChange={set('siret')} placeholder="Optionnel" style={inp} data-testid="contract-siret" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label style={lbl}>Prénom</label><input value={form.firstName} onChange={set('firstName')} style={inp} required data-testid="contract-firstName" /></div>
            <div><label style={lbl}>Nom</label><input value={form.lastName} onChange={set('lastName')} style={inp} required data-testid="contract-lastName" /></div>
          </div>
          <div><label style={lbl}>Email</label><input type="email" value={form.email} onChange={set('email')} style={inp} required data-testid="contract-email" /></div>
          <div><label style={lbl}>Téléphone</label><input type="tel" value={form.phone} onChange={set('phone')} placeholder="+33 6…" style={inp} required data-testid="contract-phone" /></div>

          <p style={{ ...lbl, marginTop: 10 }}>Restaurant</p>
          <div><label style={lbl}>Nom du restaurant</label><input value={form.restaurant_name} onChange={set('restaurant_name')} style={inp} required data-testid="contract-restaurant" /></div>
          <div><label style={lbl}>Adresse</label><input value={form.address} onChange={set('address')} style={inp} required data-testid="contract-address" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label style={lbl}>CP</label><input value={form.postal_code} onChange={set('postal_code')} style={inp} required data-testid="contract-postal" /></div>
            <div><label style={lbl}>Ville</label><input value={form.city} onChange={set('city')} style={inp} required data-testid="contract-city" /></div>
            <div><label style={lbl}>Pays</label><select value={form.country} onChange={set('country')} style={{ ...inp, appearance: 'auto' }}>{COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</select></div>
          </div>

          {/* Preview toggle — see the actual contract inline before signing */}
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPreviewOpen((v) => !v)}
                className="flex-1 flex items-center justify-center gap-2 active:scale-[0.98] transition"
                style={{
                  background: '#FFFFFF',
                  color: accentDark,
                  border: `1px solid ${accent}55`,
                  borderRadius: 12,
                  padding: '11px',
                  fontSize: 13.5,
                  fontWeight: 800,
                  letterSpacing: '-0.005em',
                  boxShadow: '0 4px 12px -4px rgba(0,0,0,0.06)',
                }}
                data-testid={`contract-preview-${offer}`}
              >
                <Eye className="w-4 h-4" />
                {previewOpen ? 'Masquer le contrat' : 'Voir mon contrat complet'}
                {previewOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {previewRead && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(52,211,153,0.14)', border: '0.5px solid rgba(52,211,153,0.45)' }}
                      data-testid="contract-read-badge">
                  <Check className="w-3 h-3 text-emerald-500" strokeWidth={3.5} />
                  <span className="text-emerald-700 text-[9.5px] font-black uppercase tracking-[0.14em]">Contrat lu</span>
                </span>
              )}
            </div>

            {previewOpen && (
              <div className="mt-2.5">
                <div
                  ref={previewScrollRef}
                  onScroll={onPreviewScroll}
                  className="rounded-xl bg-white overflow-y-auto contract-preview-scope"
                  style={{
                    maxHeight: 300,
                    padding: '14px 15px',
                    border: '0.5px solid rgba(0,0,0,0.08)',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 11,
                    lineHeight: 1.55,
                    color: '#1a1a1a',
                  }}
                  data-testid={`contract-preview-scroll-${offer}`}
                >
                  {previewHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  ) : (
                    <p className="text-center text-gray-400 text-xs py-4">Chargement…</p>
                  )}
                </div>
                <p className="mt-1.5 text-[10.5px] italic px-1" style={{ color: DA.inkMuted }}>
                  💡 Défile jusqu&apos;à la fin du contrat pour valider ta lecture.
                </p>
              </div>
            )}
          </div>

          {/* Consent */}
          <div className={`rounded-xl p-3 mt-3 ${pulseChecks ? 'sign-pulse' : ''}`}
               style={{ background: accentBg, border: `1px solid ${DA.goldSoft}` }}
               data-testid="contract-consent-block">
            <p style={{ fontSize: 11, fontWeight: 800, color: DA.goldDeep, marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Coche pour signer</p>
            {[
              ['accept_terms', 'J\'ai lu et j\'accepte les 12 clauses du contrat de prestation.'],
              ['accept_price', isEconomie
                ? 'J\'accepte le tarif TTC applicable à mon pays (2 790€ FR/ES · 2 190€ MA).'
                : isConfigurateur
                  ? (prefill?.total_ht != null
                      ? `J\'accepte le total de ${new Intl.NumberFormat('fr-FR').format(prefill.total_ht)} € HT figé au moment de la signature (devis annexé sous 24h).`
                      : 'Je comprends que le prix définitif fera l\'objet d\'un devis annexé sous 24h.')
                  : 'Je comprends que le prix définitif fera l\'objet d\'un devis annexé sous 24h.'],
              ['accept_data', 'J\'autorise le traitement de mes données personnelles conformément au RGPD.'],
              ['accept_signature', 'Je certifie que ma signature électronique a valeur de signature manuscrite (Art. 1367 Code civil).'],
            ].map(([k, txt]) => (
              <label key={k} className="flex items-start gap-2.5 py-1.5 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={set(k)} className="mt-0.5 w-4 h-4" style={{ accentColor: DA.goldMain }} data-testid={`contract-check-${k}`} />
                <span style={{ fontSize: 12.5, color: DA.ink, lineHeight: 1.4, fontFamily: DA.sans }}>{txt}</span>
              </label>
            ))}
          </div>

          <div><label style={lbl}>Signature (tape ton prénom + nom)</label><input value={form.signature_name} onChange={set('signature_name')} placeholder="Ex : Camille Martin" style={{ ...inp, fontFamily: DA.serif, fontStyle: 'italic', fontSize: 18, color: DA.greenDeep }} required data-testid="contract-signature" /></div>

          {error && <p style={{ color: '#DC2626', fontSize: 12.5, fontWeight: 700 }} data-testid="contract-error">{error}</p>}

          <button type="submit" disabled={loading} className="w-full active:scale-[0.98] transition disabled:opacity-70 flex items-center justify-center gap-2 mt-2" style={{ background: DA.greenDeep, color: '#FFFFFF', padding: '15px 20px', borderRadius: 12, fontFamily: DA.sans, fontSize: 13, fontWeight: 700, boxShadow: `0 12px 28px -8px ${DA.greenDeep}88`, border: 'none', letterSpacing: '0.14em', textTransform: 'uppercase' }} data-testid={`contract-submit-${offer}`}>
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Génération PDF…</>) : (<><span style={{ color: DA.goldMain }}>✎</span> {isEconomie ? 'Signer et payer maintenant' : isConfigurateur ? 'Rejoindre la liste d\u2019attente' : 'Signer mon contrat de mission'} →</>)}
          </button>
          <p style={{ fontSize: 10.5, color: '#5A5A5A', textAlign: 'center', lineHeight: 1.4 }}>🔒 Contrat PDF envoyé à ton email. Signature Art. 1367 Code civil.</p>
        </form>
      </div>
    </div>
  );
}
